import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationAPI } from '@/services/api';
import { Bell, Check, CheckCheck, X, Trash2 } from 'lucide-react';

export default function NotificationBell({ testIdSuffix = '' }) {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const ref = useRef(null);
  const wsRef = useRef(null);
  const navigate = useNavigate();

  const loadCount = useCallback(async () => {
    try {
      const d = await notificationAPI.getUnreadCount();
      setUnreadCount(d.unread_count);
    } catch { /* ignore */ }
  }, []);

  const loadNotifications = useCallback(async () => {
    try {
      const data = await notificationAPI.getNotifications(0, 15);
      setNotifications(data);
    } catch { /* ignore */ }
  }, []);

  // WebSocket connection for real-time updates
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;

    const connectWs = () => {
      try {
        const ws = notificationAPI.connectWebSocket(token);
        ws.onmessage = (event) => {
          try {
            const msg = JSON.parse(event.data);
            if (msg.type === 'notification') {
              setUnreadCount(prev => prev + 1);
              setNotifications(prev => [msg.data, ...prev].slice(0, 15));
            }
          } catch { /* ignore */ }
        };
        ws.onclose = () => {
          // Reconnect after delay
          setTimeout(connectWs, 5000);
        };
        ws.onerror = () => { /* noop */ };
        wsRef.current = ws;
      } catch { /* ignore */ }
    };

    connectWs();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  useEffect(() => {
    loadCount();
    const interval = setInterval(loadCount, 30000);
    return () => clearInterval(interval);
  }, [loadCount]);

  useEffect(() => {
    if (open) {
      loadNotifications();
      // Opening the bell counts as "seen" — clear the badge and mark all read.
      (async () => {
        try { await notificationAPI.markAllRead(); } catch { /* ignore */ }
        setUnreadCount(0);
      })();
    }
  }, [open, loadNotifications]);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const markRead = async (id) => {
    try {
      await notificationAPI.markRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch { /* ignore */ }
  };

  const markAllRead = async () => {
    try {
      await notificationAPI.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch { /* ignore */ }
  };

  const clearOne = async (id, e) => {
    e?.stopPropagation();
    try {
      await notificationAPI.dismiss(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch { /* ignore */ }
  };

  const clearAll = async () => {
    try {
      await notificationAPI.clearAll();
      setNotifications([]);
      setUnreadCount(0);
    } catch { /* ignore */ }
  };

  const onClickNotification = async (n) => {
    if (!n.is_read) {
      try { await notificationAPI.markRead(n.id); } catch { /* ignore */ }
      setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, is_read: true } : x));
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
    if (n.link) {
      setOpen(false);
      navigate(n.link);
    }
  };

  const typeIcons = {
    screening_scheduled: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
    screening_completed: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    access_request: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
    match_found: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)} className="relative p-2 text-slate-500 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white rounded-md transition-colors" data-testid={`notification-bell${testIdSuffix ? `-${testIdSuffix}` : ''}`}>
        <Bell className="w-4 h-4" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center px-1 font-medium" data-testid="notification-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute left-1/2 -translate-x-1/2 top-10 w-80 bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-lg shadow-xl z-50 max-h-[400px] overflow-hidden" data-testid="notification-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-zinc-800/50">
            <h3 className="text-slate-900 dark:text-white text-sm font-medium">Notifications</h3>
            <div className="flex items-center gap-3">
              {unreadCount > 0 && (
                <button onClick={markAllRead} className="text-xs text-slate-500 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white flex items-center gap-1" data-testid="mark-all-read">
                  <CheckCheck className="w-3 h-3" /> Mark all read
                </button>
              )}
              {notifications.length > 0 && (
                <button onClick={clearAll} className="text-xs text-slate-500 dark:text-zinc-400 hover:text-red-600 dark:hover:text-red-400 flex items-center gap-1" data-testid="clear-all-notifications">
                  <Trash2 className="w-3 h-3" /> Clear all
                </button>
              )}
            </div>
          </div>
          <div className="overflow-y-auto max-h-[320px]">
            {notifications.length === 0 ? (
              <p className="text-slate-500 dark:text-zinc-500 text-sm text-center py-8">No notifications</p>
            ) : (
              notifications.map(n => (
                <div
                  key={n.id}
                  onClick={() => onClickNotification(n)}
                  className={`px-4 py-3 border-b border-slate-200 dark:border-zinc-800/30 last:border-0 cursor-pointer hover:bg-slate-100 dark:hover:bg-zinc-800/30 ${!n.is_read ? 'bg-slate-100 dark:bg-zinc-800/20' : ''}`}
                  data-testid={`notification-${n.id}`}
                >
                  <div className="flex items-start gap-2">
                    <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${!n.is_read ? 'bg-blue-400' : 'bg-transparent'}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-slate-600 dark:text-zinc-300 text-sm font-medium">{n.title}</p>
                      <p className="text-slate-500 dark:text-zinc-500 text-xs mt-0.5">{n.message}</p>
                      <p className="text-slate-500 dark:text-zinc-600 text-xs mt-1">{n.created_at ? timeAgo(n.created_at) : ''}</p>
                    </div>
                    <button onClick={(e) => clearOne(n.id, e)} className="p-1 text-slate-500 dark:text-zinc-500 hover:text-red-600 dark:hover:text-red-400 flex-shrink-0" data-testid={`clear-${n.id}`} title="Clear">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function timeAgo(dateStr) {
  const now = new Date();
  const d = new Date(dateStr);
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}
